import { useTranslation } from 'react-i18next';

export default function Shipping() {
  const { t } = useTranslation('common');

  const countries = [
    {
      name: t('shipping_mexico'),
      days: '12-15',
      gradient: 'from-green-600 to-green-800',
      flagImage: 'https://public.readdy.ai/ai/img_res/884bbf7075d453cc842efee04e8b99a5.jpg',
      size: 'lg:col-span-2 lg:row-span-2'
    },
    {
      name: t('shipping_guatemala'),
      days: '12-15',
      gradient: 'from-blue-500 to-blue-700',
      flagImage: 'https://readdy.ai/api/search-image?query=Guatemala%20flag%20waving%20in%20wind%2C%20light%20blue%20and%20white%20stripes%2C%20national%20flag%20of%20Guatemala%20with%20coat%20of%20arms%2C%20patriotic%20symbol%2C%20high%20quality%20flag%20photography%2C%20clear%20sky%20background&width=800&height=600&seq=flag-guatemala&orientation=landscape',
      size: 'lg:col-span-1 lg:row-span-2'
    },
    {
      name: t('shipping_salvador'),
      days: '12-15',
      gradient: 'from-orange-500 to-orange-700',
      flagImage: 'https://readdy.ai/api/search-image?query=El%20Salvador%20flag%20waving%20in%20wind%2C%20blue%20and%20white%20horizontal%20stripes%2C%20national%20flag%20of%20El%20Salvador%20with%20coat%20of%20arms%2C%20patriotic%20symbol%2C%20high%20quality%20flag%20photography%2C%20clear%20sky%20background&width=800&height=600&seq=flag-salvador&orientation=landscape',
      size: 'lg:col-span-2 lg:row-span-1'
    },
    {
      name: t('shipping_honduras'),
      days: '12-15',
      gradient: 'from-cyan-500 to-cyan-700',
      flagImage: 'https://readdy.ai/api/search-image?query=Honduras%20flag%20waving%20in%20wind%2C%20blue%20and%20white%20horizontal%20stripes%20with%20five%20stars%2C%20national%20flag%20of%20Honduras%2C%20patriotic%20symbol%2C%20high%20quality%20flag%20photography%2C%20clear%20sky%20background&width=800&height=600&seq=flag-honduras&orientation=landscape',
      size: 'lg:col-span-1 lg:row-span-1'
    }
  ];

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: t('shipping_feature1_title'),
      description: t('shipping_feature1_desc'),
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: 'ri-map-pin-time-line',
      title: t('shipping_feature2_title'),
      description: t('shipping_feature2_desc'),
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: 'ri-box-3-line',
      title: t('shipping_feature3_title'),
      description: t('shipping_feature3_desc'),
      color: 'bg-orange-100 text-orange-600'
    }
  ];

  return (
    <section id="paqueteria" className="py-20 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-16">
          <div className="inline-flex items-center gap-2 bg-green-700 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="ri-box-3-line text-lg"></i>
            {t('shipping_badge')}
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('shipping_title')} <span className="font-serif italic text-orange-600">{t('shipping_title_highlight')}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl">
            {t('shipping_subtitle')}
          </p>
        </div>

        {/* Countries Grid with Paquetería Image */}
        <div className="grid lg:grid-cols-4 gap-6 mb-16">
          {/* Countries Grid - Left Side */}
          <div className="lg:col-span-3 grid lg:grid-cols-3 gap-6">
            {countries.map((country, index) => (
              <div
                key={index}
                className={`${country.size} rounded-3xl relative overflow-hidden group hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl cursor-pointer`}
              >
                {/* Flag Background Image */}
                <div className="absolute inset-0 w-full h-full">
                  <img 
                    src={country.flagImage}
                    alt={`${country.name} flag`}
                    className="w-full h-full object-cover"
                  />
                  {/* Dark Overlay for better text readability */}
                  <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/60"></div>
                </div>

                {/* Decorative Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-16 translate-x-16"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-12 -translate-x-12"></div>
                </div>

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col justify-between p-8 text-white">
                  <div>
                    <h3 className="text-2xl lg:text-3xl font-bold mb-2 drop-shadow-lg">{country.name}</h3>
                    <div className="flex items-center gap-2 text-white/95">
                      <i className="ri-checkbox-circle-line"></i>
                      <span className="text-sm font-medium">{t('shipping_available')}</span>
                    </div>
                  </div>
                  <div className="mt-6">
                    <div className="inline-block bg-white/30 backdrop-blur-md px-4 py-2 rounded-full border border-white/40">
                      <span className="text-sm font-semibold drop-shadow-md">{country.days} {t('shipping_days')}</span>
                    </div>
                  </div>
                </div>

                {/* Shine Effect on Hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </div>
            ))}
          </div>

          {/* Paquetería Hernández Image - Right Side */}
          <div className="lg:col-span-1 rounded-3xl overflow-hidden shadow-2xl h-full">
            <img 
              src="https://static.readdy.ai/image/44113e0bd02cf9d4b6e5b2a3bf2fb8ec/dad70e40cb1d7d4a22a82084a3ec232b.png"
              alt="Paquetería Hernández - Servicio de envíos"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Features */}
        <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12">
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className={`w-16 h-16 ${feature.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <i className={`${feature.icon} text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <a
              href="https://wa.me/13172790334?text=Hola,%20quiero%20cotizar%20un%20envío"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              {t('shipping_cta')}
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
